import React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "../../common/Icon/Icon";
import { type GraphDependantProps, useGraphFromPropsOrContext } from "../GraphProvider/GraphProvider";
import { type ReleaseDependantProps, useReleaseFromPropsOrContext } from "../ReleaseProvider/ReleaseProvider";
import type { GraphNode } from "../../../utils";
import { getIdentifier } from "../../../utils";
import SplitPane from "../../common/SplitPane/SplitPane";
import Tree, { type TreeNodeData } from "../../common/Tree/Tree";
import TextInput from "../../form/TextInput/TextInput";
import Button from "../../common/Button/Button";
import GraphDataListItem from "./GraphDataListItem";
import NodePath from "../NodePath/NodePath";
import GraphDataTreeItem from "./GraphDataTreeItem";
import { mappingDataToTreeItem } from "./internal/mapping";

export interface GraphDataProps extends GraphDependantProps, ReleaseDependantProps {
  debug?: boolean;
  selectedNodeId?: string | null;
  onSelectNode?: (node: GraphNode) => void;
  /** Optional render function to inject extra context menu items for right labels */
  renderExtraMenuItems?: (node: GraphNode) => React.ReactNode;
}

// Styles migrated to GraphData.scss

// GraphDataListItem moved to its own file

export interface GraphDataTreeNodeInfo {
  type: "instance" | "entity" | "attribute" | "global";
  text: string;
  value?: unknown;
  warnings?: number;
  errors?: number;
  containsWarnings?: boolean;
  containsErrors?: boolean;
  nodeId?: string;
}

export type GraphDataTreeNodeData = TreeNodeData<GraphDataTreeNodeInfo>;

const GraphData = (props: GraphDataProps) => {
  const { debug = true, selectedNodeId = null, onSelectNode, renderExtraMenuItems } = props;
  const { release, withReleaseProvider } = useReleaseFromPropsOrContext(props);
  const { graph, withGraphProvider } = useGraphFromPropsOrContext(props);

  const [leftSelectedId, leftSetSelectedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [allExpanded, setAllExpanded] = useState<boolean>(true);

  const root = useMemo(
    () => (graph && release ? mappingDataToTreeItem(graph, debug, release) : null),
    [graph, debug, release],
  );

  const findById = useCallback((item: any, id: string): any | null => {
    if (item.id === id) return item;
    for (const child of item.children || []) {
      const found = findById(child, id);
      if (found) return found;
    }
    return null;
  }, []);

  // Get the searchable text for a node (same as what's shown in the tree)
  const getNodeSearchableText = useCallback((node: any): string => {
    if (!node) {
      return "";
    }
    // Match the mapping: validation message when present, else node.description
    const text = node.validation
      ? `(${node.validation.severity}) ${node.validation.message}`
      : (node.description ?? "");
    return String(text).toLowerCase();
  }, []);

  // Find all matching nodes by searching descriptions (and validation messages)
  // Store both GUIDs and paths (node.path || node.id) for matching
  const matchingNodeIds = useMemo(() => {
    if (!graph || !searchTerm.trim()) {
      return new Set<string>();
    }

    const matches = new Set<string>();
    const searchLower = searchTerm.toLowerCase().trim();

    for (const nodeId of graph.nodes()) {
      const node = graph.node(nodeId);
      if (!node || node.hidden) {
        continue;
      }

      // Search on the same text that's displayed in the tree
      const searchableText = getNodeSearchableText(node);
      if (searchableText.includes(searchLower)) {
        matches.add(nodeId);
        const treeNodeId = node.path || node.id;
        if (treeNodeId) {
          matches.add(treeNodeId);
        }
      }
    }

    return matches;
  }, [graph, searchTerm, getNodeSearchableText]);

  // Track which GUIDs match (for building tree paths)
  const matchingNodeGuids = useMemo(() => {
    if (!graph || !searchTerm.trim()) {
      return new Set<string>();
    }

    const matches = new Set<string>();
    const searchLower = searchTerm.toLowerCase().trim();

    for (const nodeId of graph.nodes()) {
      const node = graph.node(nodeId);
      if (!node || (debug && node.hidden)) {
        continue;
      }

      const searchableText = getNodeSearchableText(node);
      if (searchableText.includes(searchLower)) {
        matches.add(nodeId);
      }
    }

    return matches;
  }, [graph, searchTerm, getNodeSearchableText]);

  // Get tree paths for all matching nodes by walking the tree structure
  // This ensures we use the actual tree node IDs rather than trying to reconstruct them
  const matchingTreePaths = useMemo(() => {
    if (!root || matchingNodeGuids.size === 0) {
      return new Set<string>();
    }

    const paths = new Set<string>();
    // Walk the tree and find all ancestors of matching attribute nodes
    const walkTree = (item: any, ancestors: string[]): void => {
      if (!item) return;

      if (item.info?.type === "attribute") {
        // Check if this attribute matches (by its nodeId which is the graph node id)
        const nodeId = item.info.nodeId || item.id;
        if (matchingNodeGuids.has(nodeId)) {
          // Add all ancestors (entities/instances) to paths
          for (const ancestorId of ancestors) {
            paths.add(ancestorId);
          }
          // Also add the attribute itself (for completeness)
          paths.add(item.id);
        }
        return;
      }

      // For non-attribute nodes (global, entity, instance), recurse into children
      const newAncestors = [...ancestors, item.id];
      for (const child of item.children || []) {
        walkTree(child, newAncestors);
      }
    };

    walkTree(root, []);
    return paths;
  }, [root, matchingNodeGuids]);

  // Filter tree to only show paths that contain matching nodes, and remove attributes from tree
  const filteredRoot = useMemo(() => {
    if (!root) {
      return null;
    }

    const filterTree = (item: any): any | null => {
      if (!item) {
        return null;
      }

      // Attributes should only appear in the right panel, never in the left tree
      if (item.info?.type === "attribute") {
        return null;
      }

      // Always keep Global
      if (item.id === "global") {
        const filteredChildren = (item.children || [])
          .map((child: any) => filterTree(child))
          .filter((child: any) => child !== null);

        return {
          ...item,
          children: filteredChildren,
        };
      }

      // If search is active, filter by matching paths
      if (searchTerm.trim()) {
        // Check if this path or any descendant path matches
        const checkPathMatches = (node: any): boolean => {
          if (!node) {
            return false;
          }

          // Check if this exact path matches
          if (matchingTreePaths.has(node.id)) {
            return true;
          }

          // Check if any child path matches
          if (node.children) {
            for (const child of node.children) {
              if (checkPathMatches(child)) {
                return true;
              }
            }
          }

          return false;
        };

        if (checkPathMatches(item)) {
          const filteredChildren = (item.children || [])
            .map((child: any) => filterTree(child))
            .filter((child: any) => child !== null);

          return {
            ...item,
            children: filteredChildren,
          };
        }

        return null;
      }

      // No search active, just remove attributes
      const filteredChildren = (item.children || [])
        .map((child: any) => filterTree(child))
        .filter((child: any) => child !== null);

      return {
        ...item,
        children: filteredChildren,
      };
    };

    return filterTree(root);
  }, [root, searchTerm, matchingTreePaths]);

  type RightPanelItem = { type: "instance-header"; path: string } | { type: "attribute"; treeNode: any };

  const rightItems = useMemo((): RightPanelItem[] => {
    if (!root) {
      return [];
    }

    const parent = leftSelectedId ? findById(root, leftSelectedId) : null;
    if (!parent) return [];

    const searchLower = searchTerm.trim() ? searchTerm.toLowerCase().trim() : "";
    const filterAttribute = (treeNode: any): boolean => {
      if (!searchLower || !graph) return true;
      const nodeId = treeNode.info?.nodeId;
      if (!nodeId) return false;
      const node = graph.node(nodeId);
      if (!node) return false;
      return getNodeSearchableText(node).includes(searchLower);
    };

    // Entity selected: debug = instance children (group by instance with headers); non-debug = attribute children (no instance level, no headers)
    if (parent.info?.type === "entity") {
      const hasInstanceChildren = (parent.children || []).some((c: any) => c.info?.type === "instance");
      if (hasInstanceChildren) {
        // Debug mode: entity -> instances -> attributes; show InstanceIndex header per instance
        const groups: { path: string; attributes: any[] }[] = [];
        for (const instanceNode of parent.children || []) {
          if (instanceNode.info?.type !== "instance") continue;
          const attrs = (instanceNode.children || []).filter((c: any) => c.info?.type === "attribute");
          const filtered = searchLower ? attrs.filter(filterAttribute) : attrs;
          if (filtered.length > 0) {
            groups.push({ path: instanceNode.id, attributes: filtered });
          }
        }
        const list: RightPanelItem[] = [];
        for (const g of groups) {
          list.push({ type: "instance-header", path: g.path });
          for (const attr of g.attributes) {
            list.push({ type: "attribute", treeNode: attr });
          }
        }
        return list;
      }
      // Non-debug: entity children are attributes directly; show them with no instance header
      const attrs = (parent.children || []).filter((c: any) => c.info?.type === "attribute");
      const filtered = searchLower ? attrs.filter(filterAttribute) : attrs;
      return filtered.map((attr: any) => ({ type: "attribute" as const, treeNode: attr }));
    }

    // Instance (or global) selected: single group with one header then attributes
    const attrs = (parent.children || []).filter((c: any) => c.info?.type === "attribute");
    const filtered = searchLower ? attrs.filter(filterAttribute) : attrs;
    const list: RightPanelItem[] = [];
    if (parent.info?.type === "instance") {
      list.push({ type: "instance-header", path: parent.id });
    }
    for (const attr of filtered) {
      list.push({ type: "attribute", treeNode: attr });
    }
    return list;
  }, [root, leftSelectedId, findById, searchTerm, graph, getNodeSearchableText]);

  // Handle selectedNodeId changes - expand path and select in tree (keep list visible)
  useEffect(() => {
    if (!graph || !selectedNodeId) {
      return;
    }
    let node = graph.node?.(selectedNodeId);
    if (!node) {
      for (const id of graph.nodes()) {
        const n = graph.node(id);
        if (n && (n.path === selectedNodeId || n.id === selectedNodeId)) {
          node = n;
          break;
        }
      }
    }
    if (!node) {
      return;
    }
    let pathStr: string;
    if (node.entity === "global") {
      pathStr = "global";
    } else {
      // Tree IDs are: "global", "entity_name", "entity_name/0" (NOT prefixed with "global/")
      const viewPath = node.path?.split("/").slice(0, -1) || [];
      if (viewPath.length > 0) {
        // e.g. ["employees", "1"] -> "employees/1"
        pathStr = viewPath.join("/");
      } else if (node.entity) {
        // Non-debug or no path: tree has global -> entity (no instance level)
        pathStr = node.entity;
      } else {
        return;
      }
    }
    leftSetSelectedId(pathStr);

    // Ensure tree is expanded so the selected path is visible
    // The tree will remount with openByDefault=true when allExpanded changes
    if (!allExpanded) {
      setAllExpanded(true);
    }
  }, [graph, selectedNodeId, allExpanded]);

  if (!graph || !release || !root) return null;

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  return withReleaseProvider(
    withGraphProvider(
      <div
        data-deci-graph-data="true"
        data-deci-part="root"
        style={{ width: "100%", height: "100%" }}
      >
        <SplitPane
          split="vertical"
          gutterSize={6}
        >
          <SplitPane.Pane
            preferredSize="28%"
            minSize={220}
          >
            <div
              data-deci-part="left-pane-content"
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                gridTemplateRows: "auto 1fr",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "0.5rem",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div style={{ position: "relative", marginBottom: "0.5rem" }}>
                  <Icon
                    name="search"
                    style={{
                      position: "absolute",
                      left: "0.5rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--muted-foreground)",
                      pointerEvents: "none",
                    }}
                  />
                  <TextInput
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                    placeholder="Search nodes..."
                    fullWidth
                    style={{
                      paddingLeft: "2rem",
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", fontSize: "0.875rem" }}>
                  <button
                    onClick={() => setAllExpanded(true)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textDecoration: "underline",
                      padding: 0,
                    }}
                  >
                    Expand all
                  </button>
                  <span style={{ color: "var(--muted-foreground)" }}>|</span>
                  <button
                    onClick={() => setAllExpanded(false)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textDecoration: "underline",
                      padding: 0,
                    }}
                  >
                    Collapse all
                  </button>
                </div>
              </div>
              <div
                data-deci-part="left-pane-tree-wrapper"
                style={{
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <Tree
                  key={`tree-${allExpanded ? "expanded" : "collapsed"}`}
                  openByDefault={allExpanded}
                  data={filteredRoot ? [filteredRoot] : []}
                  selectedId={leftSelectedId}
                  onSelect={(id) => leftSetSelectedId(id)}
                >
                  {GraphDataTreeItem as any}
                </Tree>
              </div>
            </div>
          </SplitPane.Pane>
          <SplitPane.Pane minSize={320}>
            <div
              data-deci-part="right-pane-wrapper"
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {searchTerm.trim() ? (
                <div
                  data-deci-part="search-banner"
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "var(--muted)",
                    borderBottom: "1px solid var(--border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.5rem",
                    flexShrink: 0,
                    position: "relative",
                    zIndex: 10,
                  }}
                >
                  <span style={{ fontSize: "0.875rem", color: "var(--muted-foreground)" }}>
                    Only showing results that match &apos;{searchTerm}&apos;
                  </span>
                  <Button
                    icon
                    label="Clear search"
                    onClick={handleClearSearch}
                    style={{ flexShrink: 0 }}
                  >
                    <Icon name="close" />
                  </Button>
                </div>
              ) : null}
              <div
                data-deci-part="right-pane-content"
                style={{
                  flex: 1,
                  overflow: "auto",
                  minHeight: 0,
                  position: "relative",
                }}
              >
                {rightItems.length === 0 && searchTerm.trim() ? (
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    No results found matching &apos;{searchTerm}&apos;
                  </div>
                ) : (
                  rightItems.map((it: RightPanelItem, index: number) => {
                    if (it.type === "instance-header") {
                      return (
                        <React.Fragment key={`header-${it.path}-${index}`}>
                          {index > 0 ? (
                            <hr
                              style={{
                                margin: 0,
                                border: "none",
                                borderTop: "1px solid var(--border)",
                              }}
                            />
                          ) : null}
                          <div
                            data-deci-part="graphdata-instance-header"
                            style={{
                              padding: "0.75rem 1rem",
                              backgroundColor: "var(--muted)",
                              borderBottom: "1px solid var(--border)",
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                            }}
                          >
                            <NodePath
                              node={{
                                path: it.path,
                                description: graph ? (getIdentifier(it.path, graph) ?? undefined) : undefined,
                              }}
                              showDescription={true}
                            />
                          </div>
                        </React.Fragment>
                      );
                    }
                    const nodeId = it.treeNode.info?.nodeId ?? it.treeNode.id;
                    return (
                      <React.Fragment key={nodeId ?? index}>
                        {index > 0 ? (
                          <hr
                            style={{
                              margin: 0,
                              border: "none",
                              borderTop: "1px solid var(--border)",
                            }}
                          />
                        ) : null}
                        <GraphDataListItem
                          id={nodeId}
                          graph={graph}
                          selectedNodeId={selectedNodeId || null}
                          onSelectNode={onSelectNode}
                          renderExtraMenuItems={renderExtraMenuItems}
                          debug={debug}
                        />
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            </div>
          </SplitPane.Pane>
        </SplitPane>
      </div>,
    ),
  );
};

export default GraphData;
